import React, { useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CloudUpload,
  Delete,
  DeleteForever,
  Edit,
  ExpandMore,
} from "@mui/icons-material";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { v4 as uuidv4 } from "uuid";
import {
  ComsActivityLine,
  ComsCatalogItem,
  ComsDailyReport,
  useCreateComsCatalogItemMutation,
  useCreateComsDailyReportMutation,
  useCreateComsUploadAuditMutation,
  useDeleteComsCatalogItemMutation,
  useDeleteComsDailyReportMutation,
  useGetComsCatalogQuery,
  useGetComsDailyReportsQuery,
  useGetComsUploadAuditQuery,
  useUpdateComsCatalogItemMutation,
} from "../../redux/comsApi";

const COMS_NAVY = "#123456";

export { BILLS } from "./boq-constants";
import { BILLS, SECTION_TO_BILL } from "./boq-constants";

// ─── CSV helpers ──────────────────────────────────────────────────────────────

/**
 * Fuzzy description matcher: returns the fraction of `query` tokens that
 * appear in `target`. Used when exact description matching fails.
 * Score of 1.0 means every word in query was found in target.
 */
function descriptionMatchScore(query: string, target: string): number {
  const tokenize = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]/g, " ")
      .split(/\s+/)
      .filter(Boolean);
  const qTokens = tokenize(query);
  if (!qTokens.length) return 0;
  const tSet = new Set(tokenize(target));
  const hits = qTokens.filter((t) => tSet.has(t)).length;
  return hits / qTokens.length;
}

function parseCsvRow(line: string): string[] {
  const result: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      result.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}

interface BulkItem {
  section: string;
  billNumber: number;
  description: string;
  unit: string;
  rate: number;
  plannedQty: number;
  displayOrder: number;
}

function parseBOQCsv(text: string): BulkItem[] {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) throw new Error("Empty CSV");
  const header = parseCsvRow(lines[0]);
  const idx = {
    row_type: header.indexOf("row_type"),
    section: header.indexOf("section"),
    description: header.indexOf("description"),
    unit: header.indexOf("unit"),
    rate: header.indexOf("rate"),
    quantity: header.indexOf("quantity"),
  };
  if (idx.row_type < 0) throw new Error("Missing row_type column in CSV");
  if (idx.section < 0 || idx.description < 0)
    throw new Error("Missing section or description column in CSV");

  const sectionOrder: string[] = [];
  const items: BulkItem[] = [];
  const sectionCounters: Record<string, number> = {};

  for (let i = 1; i < lines.length; i++) {
    const row = parseCsvRow(lines[i]);
    const rt = row[idx.row_type]?.trim();
    if (rt === "SECTION_HEADER") {
      const sectionName = row[idx.section]?.trim();
      if (sectionName && !sectionOrder.includes(sectionName)) {
        sectionOrder.push(sectionName);
      }
    } else if (rt === "ITEM") {
      const section = row[idx.section]?.trim() || "";
      // Bill number from lookup table; fall back to positional order in the CSV
      const sectionPos = sectionOrder.indexOf(section) + 1;
      const billNumber =
        SECTION_TO_BILL[section] ?? (sectionPos > 0 ? sectionPos : 0);
      if (!sectionCounters[section]) sectionCounters[section] = 0;
      const displayOrder = sectionCounters[section]++;
      const plannedQty =
        idx.quantity >= 0 ? parseFloat(row[idx.quantity]) || 0 : 0;
      items.push({
        section,
        billNumber,
        description: row[idx.description]?.trim() || "",
        unit: row[idx.unit]?.trim() || "",
        rate: parseFloat(row[idx.rate]) || 0,
        plannedQty,
        displayOrder,
      });
    }
  }
  if (!items.length) throw new Error("No ITEM rows found in CSV");
  return items;
}

// ─── XLSX actuals parser ──────────────────────────────────────────────────────
// Per-row data returned by the actuals XLSX parser
interface ActualsRow {
  description: string;
  /** null = column not present in sheet or cell was empty */
  plannedQty: number | null;
  actualQty: number | null;
}

/**
 * Detect header row + columns in a set of worksheet rows and return ActualsRow[].
 * Planned Qty: any header containing "quant" or "qty" but NOT "actual".
 * Actual Qty:  any header containing "actual" AND ("quant" or "qty").
 */
function parseSheetRows(rows: any[][]): ActualsRow[] {
  if (!rows.length) return [];
  const isQtyLike = (h: string) => h.includes("quant") || h.includes("qty");
  const isActualCol = (h: string) => h.includes("actual") && isQtyLike(h);
  const isPlannedCol = (h: string) => !h.includes("actual") && isQtyLike(h);

  let headerRowIdx = -1;
  let descCol = -1,
    plannedCol = -1,
    actualCol = -1;

  // Scan first 10 rows for a row that contains at least one qty-like column header
  for (let r = 0; r < Math.min(rows.length, 10); r++) {
    const row = rows[r];
    if (!row) continue;
    let lDesc = -1,
      lPlanned = -1,
      lActual = -1;
    for (let c = 0; c < row.length; c++) {
      const h = String(row[c] ?? "")
        .toLowerCase()
        .trim();
      if (!h) continue;
      if (lActual < 0 && isActualCol(h)) lActual = c;
      if (lPlanned < 0 && isPlannedCol(h)) lPlanned = c;
      if (
        lDesc < 0 &&
        (h.includes("desc") ||
          h.includes("activ") ||
          h.includes("work") ||
          h.includes("name") ||
          // "item" alone is OK, but "item no" / "item number" / "item #" are
          // numbering/code columns — exclude them so they are not mistaken for
          // the description column.
          (h.includes("item") &&
            !h.includes("no") &&
            !h.includes("num") &&
            !h.includes("#") &&
            !h.includes("code")))
      )
        lDesc = c;
    }
    if (lActual >= 0 || lPlanned >= 0) {
      headerRowIdx = r;
      descCol = lDesc;
      plannedCol = lPlanned;
      actualCol = lActual;
      break;
    }
  }
  if (headerRowIdx < 0) return [];

  // Fallback description column: first non-numeric label before the qty cols
  if (descCol < 0) {
    const firstQtyIdx = [plannedCol, actualCol]
      .filter((c) => c >= 0)
      .reduce((a, b) => Math.min(a, b), 99);
    const hRow = rows[headerRowIdx] ?? [];
    for (let c = 0; c < firstQtyIdx; c++) {
      const h = String(hRow[c] ?? "")
        .toLowerCase()
        .trim();
      if (
        h &&
        !h.match(/^[\d.]+$/) &&
        h !== "no" &&
        h !== "#" &&
        !h.includes("unit")
      ) {
        descCol = c;
        break;
      }
    }
    if (descCol < 0) descCol = firstQtyIdx > 0 ? firstQtyIdx - 1 : 0;
  }

  const toNum = (v: any): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = parseFloat(String(v));
    return isNaN(n) ? null : n;
  };

  const result: ActualsRow[] = [];
  for (let r = headerRowIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row) continue;
    const desc = String(row[descCol] ?? "").trim();
    if (!desc) continue;
    const dl = desc.toLowerCase();
    if (
      dl === "total" ||
      dl === "sub-total" ||
      dl === "subtotal" ||
      dl === "grand total"
    )
      continue;
    const planned = plannedCol >= 0 ? toNum(row[plannedCol]) : null;
    const actual = actualCol >= 0 ? toNum(row[actualCol]) : null;
    if (planned !== null || actual !== null) {
      result.push({
        description: desc,
        plannedQty: planned,
        actualQty: actual,
      });
    }
  }
  return result;
}

/**
 * Parse a BOQ actuals XLSX file.
 * Supports:
 *   • Multi-sheet workbooks: each sheet matched to a known section by name.
 *   • Single-sheet workbooks whose name matches a known section.
 *   • Single-sheet "stacked" layout: section-header rows (first cell = section
 *     name, all other cells blank) separate groups of item rows.
 * Returns: sectionName → ActualsRow[]
 */
function parseActualsXlsx(
  file: File,
  knownSections: string[],
): Promise<Record<string, ActualsRow[]>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const result: Record<string, ActualsRow[]> = {};
        const norm = (s: string) => s.toLowerCase().trim();

        /** Find the known section whose normalised name best matches sheetName. */
        const matchSection = (sheetName: string): string | null => {
          const sn = norm(sheetName);
          return (
            knownSections.find((ks) => {
              const kn = norm(ks);
              return kn === sn || kn.includes(sn) || sn.includes(kn);
            }) ?? null
          );
        };

        if (wb.SheetNames.length === 1) {
          const sheetName = wb.SheetNames[0];
          const ws = wb.Sheets[sheetName];
          const rows: any[][] = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            defval: null,
          });

          const direct = matchSection(sheetName);
          if (direct) {
            // Single sheet whose name matches a section directly
            const items = parseSheetRows(rows);
            if (items.length > 0) result[direct] = items;
          } else {
            // Try stacked-sections layout: rows whose first cell equals a known
            // section name and whose remaining cells are all blank are section
            // separator rows.
            let currentSection: string | null = null;
            let buffer: any[][] = [];
            const flush = () => {
              if (currentSection && buffer.length > 0) {
                const items = parseSheetRows(buffer);
                if (items.length > 0) result[currentSection] = items;
              }
            };
            for (const row of rows) {
              if (!row || !Array.isArray(row)) continue;
              const firstCell = String(row[0] ?? "").trim();
              const hasOtherData = row
                .slice(1)
                .some(
                  (v) =>
                    v !== null && v !== undefined && String(v).trim() !== "",
                );
              const sec = firstCell ? matchSection(firstCell) : null;
              if (sec && !hasOtherData) {
                flush();
                currentSection = sec;
                buffer = [];
              } else if (currentSection) {
                buffer.push(row);
              }
            }
            flush();

            // If stacked detection found nothing, treat the entire sheet as one block
            if (Object.keys(result).length === 0) {
              const items = parseSheetRows(rows);
              const key =
                knownSections.length === 1 ? knownSections[0] : sheetName;
              if (items.length > 0) result[key] = items;
            }
          }
        } else {
          // Multi-sheet workbook: match each sheet to a known section
          for (const sheetName of wb.SheetNames) {
            const ws = wb.Sheets[sheetName];
            const rows: any[][] = XLSX.utils.sheet_to_json(ws, {
              header: 1,
              defval: null,
            });
            const sectionKey = matchSection(sheetName) ?? sheetName;
            const items = parseSheetRows(rows);
            if (items.length > 0) result[sectionKey] = items;
          }
        }
        resolve(result);
      } catch (err: any) {
        reject(new Error(`Failed to parse XLSX: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read XLSX file"));
    reader.readAsArrayBuffer(file);
  });
}

// ─── Import BOQ dialog ────────────────────────────────────────────────────────
interface ValidationSummary {
  itemsImported: number;
  sectionsFound: number;
  sectionsMatched: number;
  itemsMatched: number;
  openingEntriesCreated: number;
  /** XLSX row descriptions that had no matching catalog item */
  unmatchedRows: string[];
}

interface ImportBOQDialogProps {
  blockId: string;
  open: boolean;
  onClose: () => void;
}

const ImportBOQDialog: React.FC<ImportBOQDialogProps> = ({
  blockId,
  open,
  onClose,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [openingDate, setOpeningDate] = useState("2026-01-01");
  const [status, setStatus] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [validationSummary, setValidationSummary] =
    useState<ValidationSummary | null>(null);
  const csvRef = useRef<HTMLInputElement>(null);
  const xlsxRef = useRef<HTMLInputElement>(null);

  const [createItem] = useCreateComsCatalogItemMutation();
  const [updateItem] = useUpdateComsCatalogItemMutation();
  const [createReport] = useCreateComsDailyReportMutation();
  const [logAudit] = useCreateComsUploadAuditMutation();
  const { data: existingItems = [] } = useGetComsCatalogQuery({
    blockId,
    type: "activity",
  });

  const reset = () => {
    setCsvFile(null);
    setXlsxFile(null);
    setStatus(null);
    setProgressPct(0);
    setIsRunning(false);
    setValidationSummary(null);
  };

  const doImport = async () => {
    if (!csvFile) return;
    setIsRunning(true);
    try {
      setStatus("Parsing CSV…");
      setProgressPct(5);

      const text = await csvFile.text();
      const items = parseBOQCsv(text);

      // Point 10 — validate for duplicate description within the same section
      const sectionDescMap: Record<string, Set<string>> = {};
      const dupWarnings: string[] = [];
      for (const item of items) {
        const key = item.section;
        if (!sectionDescMap[key]) sectionDescMap[key] = new Set();
        const normDesc = item.description.trim().toLowerCase();
        if (sectionDescMap[key].has(normDesc)) {
          dupWarnings.push(`[${item.section}] "${item.description}"`);
        } else {
          sectionDescMap[key].add(normDesc);
        }
      }
      if (dupWarnings.length > 0) {
        throw new Error(
          `Duplicate item descriptions found within the same section — this creates ambiguity about which row applies.\n` +
            `Please fix the following duplicates in the CSV before importing:\n` +
            dupWarnings.map((d, i) => `${i + 1}. ${d}`).join("\n"),
        );
      }

      // Collect known section names so the XLSX parser can detect stacked layouts
      const knownSections = [...new Set(items.map((i) => i.section))];

      let actuals: Record<string, ActualsRow[]> = {};
      if (xlsxFile) {
        setStatus("Parsing XLSX…");
        setProgressPct(10);
        actuals = await parseActualsXlsx(xlsxFile, knownSections);
      }

      // Build per-section lookups as items are created.
      // sectionMap        : description → CIEntry  (for exact match)
      // sectionOrderedItems: ordered array of CIEntry per section (for positional match)
      type CIEntry = {
        id: string;
        description: string;
        unit: string;
        rate: number;
        csvPlannedQty: number;
      };
      const sectionMap: Record<string, Map<string, CIEntry>> = {};
      const sectionOrderedItems: Record<string, CIEntry[]> = {};

      const total = items.length;
      let done = 0;

      for (const item of items) {
        const result = await createItem({
          blockId,
          type: "activity",
          name: item.description,
          unit: item.unit,
          defaultRate: item.rate,
          billNumber: item.billNumber || undefined,
          plannedQty: item.plannedQty,
          displayOrder: item.displayOrder,
        }).unwrap();

        const ciEntry: CIEntry = {
          id: result.id!,
          description: item.description,
          unit: item.unit,
          rate: item.rate,
          csvPlannedQty: item.plannedQty,
        };
        if (!sectionMap[item.section]) sectionMap[item.section] = new Map();
        sectionMap[item.section].set(
          item.description.trim().toLowerCase(),
          ciEntry,
        );
        if (!sectionOrderedItems[item.section])
          sectionOrderedItems[item.section] = [];
        sectionOrderedItems[item.section].push(ciEntry);

        done++;
        setProgressPct(10 + Math.round((done / total) * 70));
        setStatus(`Importing ${done} / ${total} items…`);
      }

      // ── Match XLSX actuals to catalog items by description (per section) ──
      let openingCount = 0;
      let sectionsMatched = 0;
      let itemsMatched = 0;
      const unmatchedRows: string[] = [];

      if (xlsxFile && Object.keys(actuals).length > 0) {
        setStatus("Matching actuals to catalog…");
        setProgressPct(85);

        const activityLines: ComsActivityLine[] = [];
        const normStr = (s: string) => s.toLowerCase().trim();
        // Track catalog IDs already claimed this import to enforce strict 1-to-1 matching.
        const claimedIds = new Set<string>();

        for (const [xlsxSection, xlsxRows] of Object.entries(actuals)) {
          // Find the catalog section that best matches this XLSX section name
          const catalogSection = Object.keys(sectionMap).find((s) => {
            const sn = normStr(xlsxSection),
              sk = normStr(s);
            return sk === sn || sk.includes(sn) || sn.includes(sk);
          });
          if (!catalogSection) continue;
          sectionsMatched++;

          const descMap = sectionMap[catalogSection];
          const orderedItems = sectionOrderedItems[catalogSection] ?? [];

          for (let xlsxIdx = 0; xlsxIdx < xlsxRows.length; xlsxIdx++) {
            const row = xlsxRows[xlsxIdx];
            const normDesc = normStr(row.description);
            let ci: CIEntry | undefined;

            // ── Strategy 1: exact description match (case-insensitive, trimmed) ──
            const exactHit = descMap.get(normDesc);
            if (exactHit && !claimedIds.has(exactHit.id)) ci = exactHit;

            // ── Strategy 2: positional match ──────────────────────────────────
            // Map XLSX row index directly to the catalog item at the same position.
            // Accept if score ≥ 0.30 AND there is no other unclaimed catalog item
            // that matches this XLSX description significantly better (gap > 0.10).
            // The "no better alternative" guard prevents "Ref C2" from stealing
            // "Ref C1"'s catalog slot: "Ref C2 catalog" scores 0.17 higher than
            // "Ref C1 catalog" for the "Ref C2" XLSX row, so the positional claim
            // is rejected and fuzzy search finds the correct item instead.
            if (!ci && xlsxIdx < orderedItems.length) {
              const candidate = orderedItems[xlsxIdx];
              if (!claimedIds.has(candidate.id)) {
                const candScore = Math.max(
                  descriptionMatchScore(
                    normDesc,
                    candidate.description.toLowerCase(),
                  ),
                  descriptionMatchScore(
                    candidate.description.toLowerCase(),
                    normDesc,
                  ),
                );
                if (candScore >= 0.3) {
                  // Check that no other unclaimed catalog item is a better fit
                  let betterExists = false;
                  for (const [, entry] of descMap.entries()) {
                    if (entry.id === candidate.id || claimedIds.has(entry.id))
                      continue;
                    const otherScore = Math.max(
                      descriptionMatchScore(
                        normDesc,
                        entry.description.toLowerCase(),
                      ),
                      descriptionMatchScore(
                        entry.description.toLowerCase(),
                        normDesc,
                      ),
                    );
                    if (otherScore > candScore + 0.1) {
                      betterExists = true;
                      break;
                    }
                  }
                  if (!betterExists) ci = candidate;
                }
              }
            }

            // ── Strategy 3: fuzzy description match (last resort) ─────────────
            // Only used when the XLSX and catalog have different row counts or
            // order. High threshold + clear-winner rule to avoid false positives.
            if (!ci) {
              let bestScore = 0;
              let secondBestScore = 0;
              let bestEntry: CIEntry | undefined;
              for (const [key, entry] of descMap.entries()) {
                if (claimedIds.has(entry.id)) continue;
                const score = Math.max(
                  descriptionMatchScore(normDesc, key),
                  descriptionMatchScore(key, normDesc),
                );
                if (score > bestScore) {
                  secondBestScore = bestScore;
                  bestScore = score;
                  bestEntry = entry;
                } else if (score > secondBestScore) {
                  secondBestScore = score;
                }
              }
              const clearWinner =
                secondBestScore === 0 || bestScore / secondBestScore >= 1.2;
              if (bestScore >= 0.85 && clearWinner) ci = bestEntry;
            }

            if (!ci) {
              unmatchedRows.push(`[${xlsxSection}] "${row.description}"`);
              continue;
            }

            // Mark this catalog item as claimed so no other XLSX row can steal it
            claimedIds.add(ci.id);

            const hasPlanned = row.plannedQty !== null;
            const hasActual = row.actualQty !== null;

            if (!hasPlanned && !hasActual) continue;

            itemsMatched++;

            // If the XLSX supplies a planned qty that the CSV omitted, update the item
            if (
              hasPlanned &&
              (row.plannedQty ?? 0) > 0 &&
              ci.csvPlannedQty === 0
            ) {
              await updateItem({
                id: ci.id,
                data: { plannedQty: row.plannedQty! },
              }).unwrap();
            }

            // Queue actual qty as an opening-balance daily report entry
            if (hasActual && (row.actualQty ?? 0) > 0) {
              activityLines.push({
                lineId: uuidv4(),
                catalogItemId: ci.id,
                name: ci.description,
                unit: ci.unit,
                quantity: row.actualQty!,
                rate: ci.rate,
                amount: +(row.actualQty! * ci.rate).toFixed(2),
              });
            }
          }
        }

        if (activityLines.length > 0) {
          await createReport({
            blockId,
            reportDate: openingDate,
            notes: "Opening balance — work completed prior to app go-live",
            activities: activityLines,
          }).unwrap();
          openingCount = activityLines.length;
        }
      }

      setProgressPct(100);

      const vSummary: ValidationSummary = {
        itemsImported: done,
        sectionsFound: Object.keys(actuals).length,
        sectionsMatched,
        itemsMatched,
        openingEntriesCreated: openingCount,
        unmatchedRows,
      };
      setValidationSummary(vSummary);

      const statusMsg = xlsxFile
        ? `Done — ${done} items imported, ${sectionsMatched}/${
            Object.keys(actuals).length
          } sections matched, ${itemsMatched} items matched.`
        : `Done — ${done} catalog items imported.`;
      setStatus(statusMsg);

      // Point 8 — log audit entry for catalogue import
      try {
        const addedList = items.map((it) => ({
          name: it.description,
          billNumber: it.billNumber,
          unit: it.unit,
          rate: it.rate,
        }));
        await logAudit({
          blockId,
          action: "catalogue-import",
          diff: { added: addedList, removedCount: 0, rateChanged: [] },
        });
      } catch {
        /* audit failure should not block the UI */
      }

      // Auto-close only when no issues were found; otherwise stay open for review
      const hasIssues = unmatchedRows.length > 0;
      if (!hasIssues) {
        setTimeout(() => {
          reset();
          onClose();
        }, 2000);
      } else {
        setIsRunning(false);
      }
    } catch (err: any) {
      const msg =
        err?.data?.error?.message ??
        err?.data?.message ??
        err?.message ??
        (err?.status ? `HTTP ${err.status}` : JSON.stringify(err));
      setStatus(`Error: ${msg}`);
      setProgressPct(0);
      setIsRunning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isRunning) {
          reset();
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: COMS_NAVY, color: "#fff" }}>
        Import BOQ from CSV
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {(existingItems as ComsCatalogItem[]).length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This block already has {(existingItems as ComsCatalogItem[]).length}{" "}
            activity items. Importing will add to the existing items —
            duplicates are not checked.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Import BOQ line items as catalog activities. Only{" "}
          <strong>ITEM</strong> rows are imported.{" "}
          <strong>SECTION_HEADER</strong> rows determine bill grouping (Bills
          1–12).
        </Typography>

        {/* CSV */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            BOQ CSV{" "}
            <Typography component="span" color="error.main">
              *
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Must have columns: row_type, section, description, unit, rate.
          </Typography>
          <input
            ref={csvRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
          />
          <Box display="flex" gap={1}>
            <TextField
              size="small"
              value={csvFile?.name ?? ""}
              placeholder="No file selected"
              inputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => csvRef.current?.click()}
            >
              Browse
            </Button>
          </Box>
        </Box>

        {/* XLSX */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            Actuals XLSX{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              (optional — creates an opening balance report)
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            One sheet per section, same item order as the CSV. Header must
            contain "Actual" and "Qty".
          </Typography>
          <input
            ref={xlsxRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={(e) => setXlsxFile(e.target.files?.[0] ?? null)}
          />
          <Box display="flex" gap={1}>
            <TextField
              size="small"
              value={xlsxFile?.name ?? ""}
              placeholder="No file selected"
              inputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => xlsxRef.current?.click()}
            >
              Browse
            </Button>
          </Box>
        </Box>

        {/* Opening balance date — only shown when XLSX is selected */}
        {xlsxFile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Opening Balance Date
            </Typography>
            <TextField
              size="small"
              type="date"
              fullWidth
              value={openingDate}
              onChange={(e) => setOpeningDate(e.target.value)}
            />
          </Box>
        )}

        {/* Progress */}
        {status && (
          <Box sx={{ mt: 2 }}>
            {progressPct > 0 && progressPct < 100 && (
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            )}
            <Typography
              variant="body2"
              color={status.startsWith("Error") ? "error" : "text.secondary"}
            >
              {status}
            </Typography>
          </Box>
        )}

        {/* Validation summary shown after import completes */}
        {validationSummary && !status?.startsWith("Error") && (
          <Box sx={{ mt: 1 }}>
            <Alert
              severity={
                validationSummary.unmatchedRows.length > 0
                  ? "warning"
                  : "success"
              }
              sx={{ fontSize: "0.78rem" }}
            >
              <Typography variant="body2" fontWeight={700} mb={0.5}>
                Import Summary
              </Typography>
              <Typography variant="body2">
                • {validationSummary.itemsImported} catalog items created
              </Typography>
              {validationSummary.sectionsFound > 0 && (
                <>
                  <Typography variant="body2">
                    • {validationSummary.sectionsMatched} /{" "}
                    {validationSummary.sectionsFound} sections matched from XLSX
                  </Typography>
                  <Typography variant="body2">
                    • {validationSummary.itemsMatched} items matched
                  </Typography>
                  {validationSummary.openingEntriesCreated > 0 && (
                    <Typography variant="body2">
                      • {validationSummary.openingEntriesCreated}{" "}
                      opening-balance entries created
                    </Typography>
                  )}
                  {validationSummary.unmatchedRows.length > 0 && (
                    <Box mt={0.5}>
                      <Typography variant="body2" color="warning.dark">
                        • ⚠ {validationSummary.unmatchedRows.length} unmatched
                        XLSX row(s) — no matching catalog item:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.25 }}>
                        {validationSummary.unmatchedRows
                          .slice(0, 5)
                          .map((r, i) => (
                            <Typography
                              component="li"
                              key={i}
                              variant="caption"
                            >
                              {r}
                            </Typography>
                          ))}
                        {validationSummary.unmatchedRows.length > 5 && (
                          <Typography component="li" variant="caption">
                            …and {validationSummary.unmatchedRows.length - 5}{" "}
                            more
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={isRunning}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={doImport}
          disabled={!csvFile || isRunning}
          startIcon={<CloudUpload />}
          sx={{ bgcolor: COMS_NAVY, "&:hover": { filter: "brightness(0.85)" } }}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TYPES = [
  { value: "activity", label: "Activities", color: "#123456" },
  { value: "material", label: "Materials", color: "#1565c0" },
  { value: "labour", label: "Labour", color: "#b71c1c" },
  { value: "equipment", label: "Equipment", color: "#bf8c00" },
] as const;

type CatalogType = (typeof TYPES)[number]["value"];

interface Props {
  blockId: string;
}

const RATE_LABEL: Record<CatalogType, string> = {
  activity: "Rate (per unit)",
  material: "Unit Price",
  labour: "Day Rate",
  equipment: "Hourly Rate",
};

const emptyForm = () => ({
  name: "",
  unit: "",
  defaultRate: "0",
  billNumber: "",
});

const Catalog: React.FC<Props> = ({ blockId }) => {
  const [activeTab, setActiveTab] = useState<CatalogType>("activity");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editItem, setEditItem] = useState<ComsCatalogItem | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");
  const [expandedBills, setExpandedBills] = useState<Record<string, boolean>>(
    {},
  );

  const { data: items = [], isLoading } = useGetComsCatalogQuery({
    blockId,
    type: activeTab,
  });
  const [createItem] = useCreateComsCatalogItemMutation();
  const [updateItem] = useUpdateComsCatalogItemMutation();
  const [deleteItem] = useDeleteComsCatalogItemMutation();
  const [deleteReport] = useDeleteComsDailyReportMutation();
  // Point 8 — audit trail
  const { data: auditEntries = [] } = useGetComsUploadAuditQuery(blockId);
  const catalogAudit = (auditEntries as any[]).filter(
    (e) => e.action === "catalogue-import",
  );
  const [auditOpen, setAuditOpen] = useState(false);

  // ── Clear Catalog state ────────────────────────────────────────────────────
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearDeleteReports, setClearDeleteReports] = useState(false);
  const [clearRunning, setClearRunning] = useState(false);
  const [clearStatus, setClearStatus] = useState<string | null>(null);
  const { data: reportsForClear = [] } = useGetComsDailyReportsQuery(blockId, {
    skip: !clearConfirmOpen,
  });

  // Group activities by bill number
  const groupedActivities = useMemo(() => {
    if (activeTab !== "activity") return null;
    const groups: Record<string, ComsCatalogItem[]> = {};
    for (const item of items as ComsCatalogItem[]) {
      const key =
        item.billNumber != null ? String(item.billNumber) : "unassigned";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    return groups;
  }, [items, activeTab]);

  const handleClearCatalog = async () => {
    setClearRunning(true);
    setClearStatus(null);

    const extractMsg = (err: any): string =>
      err?.data?.error?.message ??
      err?.data?.message ??
      err?.message ??
      (err?.status ? `HTTP ${err.status}` : JSON.stringify(err));

    let itemErrors = 0;
    let reportErrors = 0;

    // Delete catalog items — continue on individual failures
    const activityItems = items as ComsCatalogItem[];
    for (let i = 0; i < activityItems.length; i++) {
      setClearStatus(
        `Deleting catalog items… ${i + 1} / ${activityItems.length}`,
      );
      const item = activityItems[i];
      if (!item.id) continue;
      try {
        await deleteItem(item.id).unwrap();
      } catch {
        itemErrors++;
      }
    }

    // Delete daily reports — continue on individual failures
    if (clearDeleteReports) {
      const rpts = reportsForClear as ComsDailyReport[];
      for (let i = 0; i < rpts.length; i++) {
        setClearStatus(`Deleting daily reports… ${i + 1} / ${rpts.length}`);
        const rpt = rpts[i];
        if (!rpt.id) continue;
        try {
          await deleteReport(rpt.id).unwrap();
        } catch {
          reportErrors++;
        }
      }
    }

    const warnings: string[] = [];
    if (itemErrors > 0)
      warnings.push(`${itemErrors} catalog item(s) could not be deleted`);
    if (reportErrors > 0)
      warnings.push(`${reportErrors} report(s) could not be deleted`);

    setClearStatus(
      warnings.length > 0
        ? `Done with warnings: ${warnings.join("; ")}. Ready for re-import.`
        : "Done — catalog cleared. Ready for new BOQ import.",
    );
    setTimeout(() => {
      setClearConfirmOpen(false);
      setClearRunning(false);
      setClearStatus(null);
      setClearDeleteReports(false);
    }, 2500);
  };

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm());
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (item: ComsCatalogItem) => {
    setEditItem(item);
    setForm({
      name: item.name,
      unit: item.unit,
      defaultRate: String(item.defaultRate),
      billNumber: item.billNumber != null ? String(item.billNumber) : "",
    });
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.unit.trim()) {
      setError("Name and unit are required.");
      return;
    }
    if (activeTab === "activity" && !form.billNumber) {
      setError("Please select a bill number.");
      return;
    }
    const payload: Omit<ComsCatalogItem, "id"> = {
      blockId,
      type: activeTab,
      name: form.name.trim(),
      unit: form.unit.trim(),
      defaultRate: Number(form.defaultRate) || 0,
      ...(activeTab === "activity" && form.billNumber
        ? { billNumber: Number(form.billNumber) }
        : {}),
    };
    if (editItem?.id) {
      await updateItem({ id: editItem.id, data: payload });
    } else {
      await createItem(payload);
    }
    setDialogOpen(false);
  };

  const toggleBill = (key: string) =>
    setExpandedBills((prev) => ({
      ...prev,
      [key]: prev[key] !== false ? false : true,
    }));

  const currentType = TYPES.find((t) => t.value === activeTab)!;

  // Columns for non-activity tabs
  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "unit", headerName: "Unit", width: 100 },
    {
      field: "defaultRate",
      headerName: RATE_LABEL[activeTab],
      width: 160,
      type: "number",
      valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`,
    },
    {
      field: "actions",
      type: "actions",
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<Edit fontSize="small" />}
          label="Edit"
          onClick={() => openEdit(params.row as ComsCatalogItem)}
        />,
        <GridActionsCellItem
          key="del"
          icon={<Delete fontSize="small" color="error" />}
          label="Delete"
          onClick={() => deleteItem(params.id as string)}
        />,
      ],
    },
  ];

  // Reusable activity table for each bill group
  const ActivityTable: React.FC<{ rows: ComsCatalogItem[] }> = ({ rows }) => (
    <Table size="small">
      <TableHead>
        <TableRow
          sx={{
            "& th": {
              fontWeight: 700,
              bgcolor: "#fafafa",
              fontSize: "0.78rem",
            },
          }}
        >
          <TableCell>Name</TableCell>
          <TableCell width={100}>Unit</TableCell>
          <TableCell width={150} align="right">
            Rate (per unit)
          </TableCell>
          <TableCell width={88} />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((item) => (
          <TableRow key={item.id} hover>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.unit}</TableCell>
            <TableCell align="right">
              ${(item.defaultRate ?? 0).toFixed(2)}
            </TableCell>
            <TableCell align="right" sx={{ pr: 1 }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(item)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => deleteItem(item.id as string)}
                >
                  <Delete fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <Typography
        variant="h2"
        sx={{ color: COMS_NAVY, fontWeight: 600, mb: 3 }}
      >
        {"Catalog \u2014 Activities & Materials"}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{ mb: 3, "& .MuiTabs-indicator": { bgcolor: currentType.color } }}
      >
        {TYPES.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            sx={{ "&.Mui-selected": { color: t.color } }}
          />
        ))}
      </Tabs>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {/* Header bar */}
        <Box
          sx={{
            bgcolor: currentType.color,
            px: 3,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: 1,
            }}
          >
            {currentType.label.toUpperCase()}
          </Typography>
          <Box display="flex" gap={1}>
            {activeTab === "activity" &&
              (items as ComsCatalogItem[]).length > 0 && (
                <Button
                  size="small"
                  startIcon={<DeleteForever />}
                  onClick={() => setClearConfirmOpen(true)}
                  sx={{
                    color: "#ffcdd2",
                    borderColor: "rgba(255,205,210,0.4)",
                    border: "1px solid",
                  }}
                >
                  Clear Catalog
                </Button>
              )}
            {activeTab === "activity" && (
              <Button
                size="small"
                startIcon={<CloudUpload />}
                onClick={() => setImportOpen(true)}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.5)",
                  border: "1px solid",
                }}
              >
                Import BOQ
              </Button>
            )}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={openCreate}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                border: "1px solid",
              }}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        {/* â”€â”€ Activities tab â€” grouped by bill â”€â”€ */}
        {activeTab === "activity" && groupedActivities ? (
          <Box p={2}>
            {isLoading ? (
              <Typography sx={{ p: 2, color: "text.secondary" }}>
                Loadingâ€¦
              </Typography>
            ) : Object.keys(groupedActivities).length === 0 ? (
              <Typography sx={{ p: 2, color: "text.secondary" }}>
                No activities yet. Click "Add Item" to get started.
              </Typography>
            ) : (
              <>
                {BILLS.map((bill) => {
                  const billItems =
                    groupedActivities[String(bill.number)] ?? [];
                  if (billItems.length === 0) return null;
                  const key = String(bill.number);
                  const isOpen = expandedBills[key] !== false; // default expanded
                  return (
                    <Accordion
                      key={bill.number}
                      expanded={isOpen}
                      onChange={() => toggleBill(key)}
                      sx={{
                        mb: 1,
                        "&:before": { display: "none" },
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: "#f0f4ff",
                          borderLeft: `4px solid ${COMS_NAVY}`,
                          minHeight: 48,
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          width="100%"
                        >
                          <Chip
                            label={`Bill ${bill.number}`}
                            size="small"
                            sx={{
                              bgcolor: COMS_NAVY,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.7rem",
                            }}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {bill.description}
                          </Typography>
                          <Chip
                            label={billItems.length}
                            size="small"
                            sx={{
                              ml: "auto",
                              bgcolor: "#e0e7ff",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <ActivityTable rows={billItems} />
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {/* Unassigned (legacy items without a bill) */}
                {(groupedActivities["unassigned"] ?? []).length > 0 && (
                  <Accordion
                    expanded={expandedBills["unassigned"] !== false}
                    onChange={() => toggleBill("unassigned")}
                    sx={{
                      mb: 1,
                      "&:before": { display: "none" },
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        bgcolor: "#fafafa",
                        borderLeft: "4px solid #9e9e9e",
                        minHeight: 48,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        width="100%"
                      >
                        <Chip
                          label="Unassigned"
                          size="small"
                          sx={{
                            bgcolor: "#757575",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                          }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.secondary"
                        >
                          No bill assigned
                        </Typography>
                        <Chip
                          label={groupedActivities["unassigned"].length}
                          size="small"
                          sx={{
                            ml: "auto",
                            bgcolor: "#e0e0e0",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <ActivityTable rows={groupedActivities["unassigned"]} />
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
          </Box>
        ) : (
          /* â”€â”€ Other tabs â€” DataGrid â”€â”€ */
          <DataGrid
            autoHeight
            rows={(items as ComsCatalogItem[]).map((i) => ({ ...i, id: i.id }))}
            columns={columns}
            loading={isLoading}
            hideFooterSelectedRowCount
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f9f9f9",
                fontWeight: 700,
              },
            }}
          />
        )}
      </Paper>

      {/* â”€â”€ Add / Edit dialog â”€â”€ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: currentType.color, color: "#fff" }}>
          {editItem ? "Edit" : "Add"} {currentType.label.slice(0, -1)}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Typography color="error" mb={2} variant="body2">
              {error}
            </Typography>
          )}

          {/* Bill number â€” activities only */}
          {activeTab === "activity" && (
            <FormControl fullWidth required sx={{ mb: 2, mt: 1 }}>
              <InputLabel>Bill Number</InputLabel>
              <Select
                label="Bill Number"
                value={form.billNumber}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    billNumber: e.target.value as string,
                  }))
                }
              >
                {BILLS.map((b) => (
                  <MenuItem key={b.number} value={String(b.number)}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={`Bill ${b.number}`}
                        size="small"
                        sx={{
                          bgcolor: COMS_NAVY,
                          color: "#fff",
                          fontSize: "0.65rem",
                          height: 20,
                          minWidth: 52,
                        }}
                      />
                      <Typography variant="body2">{b.description}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Name"
            fullWidth
            required
            sx={{ mb: 2, ...(activeTab !== "activity" && { mt: 1 }) }}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Unit"
            fullWidth
            required
            placeholder="mÂ², mÂ³, hr, day, no."
            sx={{ mb: 2 }}
            value={form.unit}
            onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
          />
          <TextField
            label={RATE_LABEL[activeTab]}
            fullWidth
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ mb: 1 }}
            value={form.defaultRate}
            onChange={(e) =>
              setForm((f) => ({ ...f, defaultRate: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              bgcolor: currentType.color,
              "&:hover": { filter: "brightness(0.85)" },
            }}
          >
            {editItem ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── BOQ import dialog ── */}
      {/* ── Point 8: Catalogue Import Audit Trail (Activities tab only) ── */}
      {activeTab === "activity" && catalogAudit.length > 0 && (
        <Box mt={4} px={1}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", userSelect: "none", mb: 1 }}
            onClick={() => setAuditOpen((o) => !o)}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: COMS_NAVY }}
            >
              Catalogue Import History ({catalogAudit.length})
            </Typography>
            {auditOpen ? (
              <span style={{ fontSize: 12, color: COMS_NAVY }}>
                [▲ collapse]
              </span>
            ) : (
              <span style={{ fontSize: 12, color: COMS_NAVY }}>[▼ expand]</span>
            )}
          </Box>
          {auditOpen &&
            (catalogAudit as any[]).map((entry: any, i: number) => (
              <Box
                key={entry.id ?? i}
                sx={{
                  p: 1.5,
                  mb: 1,
                  bgcolor: "#fafbff",
                  border: "1px solid #e0e7ef",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  BOQ Import —{" "}
                  {entry.performedByName
                    ? `${entry.performedByName} (${entry.performedByEmail})`
                    : entry.performedByEmail ?? "Unknown user"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(entry.performedAt).toLocaleString("en-ZA", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                  {entry.diff?.added?.length != null &&
                    ` — ${entry.diff.added.length} items added`}
                </Typography>
              </Box>
            ))}
        </Box>
      )}

      <ImportBOQDialog
        blockId={blockId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />

      {/* ── Clear Catalog confirmation dialog ── */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => {
          if (!clearRunning) {
            setClearConfirmOpen(false);
            setClearDeleteReports(false);
            setClearStatus(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#c62828", color: "#fff" }}>
          Clear Catalog &amp; Delete BOQ
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" mb={2}>
            This will permanently delete all{" "}
            <strong>{(items as ComsCatalogItem[]).length}</strong> activity
            catalog items and their planned quantities from this block.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={clearDeleteReports}
                onChange={(e) => setClearDeleteReports(e.target.checked)}
                disabled={clearRunning}
                color="error"
              />
            }
            label={
              <Typography variant="body2" color="error.main">
                Also delete all
                {(reportsForClear as ComsDailyReport[]).length > 0
                  ? ` ${(reportsForClear as ComsDailyReport[]).length}`
                  : ""}{" "}
                daily reports (resets all logged work progress)
              </Typography>
            }
          />
          {clearStatus && (
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              {clearRunning && <CircularProgress size={16} />}
              <Typography
                variant="body2"
                color={
                  clearStatus.startsWith("Error") ? "error" : "text.secondary"
                }
              >
                {clearStatus}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setClearConfirmOpen(false);
              setClearDeleteReports(false);
              setClearStatus(null);
            }}
            disabled={clearRunning}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleClearCatalog}
            disabled={clearRunning}
            startIcon={
              clearRunning ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteForever />
              )
            }
          >
            Clear Catalog
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Catalog;
